import { Component, DestroyRef, inject, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	ListLayoutComponent,
	TitleGroupComponent,
	ToolbarComponent,
	TableComponent,
	IToolbar,
	ITableHeader,
	ITableConfig,
	ITableRow,
	ITableCellEmitter,
	ISelect,
	ITableSortEvent,
	TabsComponent,
	ITab,
	TableFilterComponent,
} from '@tecalisdev/infinity';
import { OperationsHelperService } from '../../services/operations-helper.service';
import { TranslateService } from '@ngx-translate/core';
import { OperationsService } from '../../services/operations.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { ActivatedRoute } from '@angular/router';
import { MasterService } from '../../../shared/services/master.service';
import { ToolbarHelperService } from '../../../shared/services/toolbar-helper.service';
import {
	DOCS_TYPE,
	IOperationListPayload,
	IOperationListResponse,
	OPERATION_TYPE,
} from '../../models/operations.models';
import { Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { formatFilterTable, transformTrimObject } from '../../../core/utils/utils';
import { buildMessagingApiPayload } from '../../utils/messaging-list-payload.util';
import { PaginatedListController } from '../../../shared/list/paginated-list.controller';
import { Location } from '@angular/common';
import { NavigationService } from '../../../shared/services/navigation.service';

@Component({
	selector: 'app-operations-list',
	imports: [
		ListLayoutComponent,
		TitleGroupComponent,
		ToolbarComponent,
		TableComponent,
		TabsComponent,
		TableFilterComponent,
	],
	templateUrl: './operations-list.component.html',
	styleUrl: './operations-list.component.scss',
})
export class OperationsListComponent extends PaginatedListController implements OnInit {
	trans = inject(TranslateService);
	operationHelper = inject(OperationsHelperService);
	operationsService = inject(OperationsService);
	loading = inject(LoadingService);
	masterService = inject(MasterService);
	toolbarHelperService = inject(ToolbarHelperService);
	activatedRoute = inject(ActivatedRoute);
	location = inject(Location);
	private navigationService = inject(NavigationService);
	private destroyRef = inject(DestroyRef);

	// TABLE
	public toolbar: Signal<IToolbar>;
	public header: ITableHeader[] = this.operationHelper.header;
	public override config = this.operationHelper.config as ITableConfig;
	public tableFilters: IOperationListPayload;
	private sort: ITableSortEvent = this.operationHelper.sort;
	public content: ITableRow[] = [];
	public operationGroup: { title: string; tooltip: string };
	public id: OPERATION_TYPE | string;
	public OPERATION_TYPE = OPERATION_TYPE;
	public tabs: ITab[] = this.operationHelper.tabs;
	private docTypeSelected?: string;
	private previousId: string | undefined;

	ngOnInit(): void {
		this.activatedRoute.paramMap.subscribe(() => {
			this.tableFilters = undefined;
			this.loading.show();
			const newId = this.activatedRoute.snapshot.paramMap.get('id')?.toUpperCase();
			if (this.previousId !== undefined && newId !== this.previousId) {
				this.docTypeSelected = undefined;
				this.config.pagination.page = this.operationHelper.defaultPagination.page;
				this.config.pagination.limit = this.operationHelper.defaultPagination.limit;
				this.toolbarHelperService.cleanSavedToolbar(this.constructor.name);
			}
			this.previousId = newId;
			this.id = this.activatedRoute.snapshot.paramMap.get('id');
			if (this.id) {
				this.id = this.id.toUpperCase();
				// Asignar el tipo de operación al helper
				this.operationHelper.operationType = this.id as OPERATION_TYPE;
				// Actualizar el header cada vez que cambia el tipo de operación
				this.header = this.operationHelper.header;
				this.sort = this.operationHelper.resetSort();

				// Si el tipo de operación no es OCR, ocultar las pestañas y limpiar el filtro de tipo de documento
				if (this.id !== OPERATION_TYPE.OCR) {
					this.tabs = []; // Ocultar pestañas
					this.docTypeSelected = undefined;
				} else {
					if (this.tabs.length === 0) this.tabs = this.operationHelper.tabs; // Mostrar pestañas si el tipo es OCR
				}
			}
			this.operationGroup = this.setOperationTitleGroup();
			if (this.id === OPERATION_TYPE.FORM) {
				this.operationHelper.loadFormWorkflowOptions();
			}
			this.updateToolbar();
		});

		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.header = this.operationHelper.header;
			this.config = { ...this.operationHelper.config, pagination: this.config.pagination };
		});
	}

	protected override beforeListRequest(): void {
		delete this.config.status;
		this.loading.showTable(this.config, this.header.length);
	}

	protected override afterListRequest(): void {
		this.loading.hideTable(this.config);
	}

	protected createListRequest(): Observable<IOperationListResponse> {
		this.getPayload();
		return this.operationsService.list(this.tableFilters);
	}

	protected applyListResponse(response: IOperationListResponse): void {
		this.content = this.operationHelper.transformRow(response.data);
	}

	protected override onListError(_error: HttpErrorResponse): void {
		this.content = [];
		this.config['status'] = { type: 'error' };
	}

	private getPayload(): void {
		this.tableFilters = formatFilterTable(this.tableFilters);
		let id = this.activatedRoute.snapshot.paramMap.get('id');
		if (id) {
			id = id.toUpperCase();
			if (id === OPERATION_TYPE.FULL) {
				this.tableFilters = {
					...this.tableFilters,
					operation_types_uuid: this.getOperationTypesUuids(
						Object.values(OPERATION_TYPE).filter(
							(operation) =>
								operation !== OPERATION_TYPE.HOME &&
								operation !== OPERATION_TYPE.FINISH &&
								operation !== OPERATION_TYPE.MESSAGING,
						),
					),
				};
			} else if (id === OPERATION_TYPE.MESSAGING) {
				// Payload TFA se resuelve al final con buildMessagingApiPayload (has_2fa + channel).
			} else {
				this.tableFilters = {
					...this.tableFilters,
					operation_types_uuid: this.getOperationTypesUuids([id]),
				};

				if (id === OPERATION_TYPE.OCR && this.docTypeSelected) {
					this.tableFilters = {
						...this.tableFilters,
						document_type: this.docTypeSelected,
					};
				}
			}
		}
		this.tableFilters = this.withPagination(this.tableFilters, {
			sort_by: this.sort.sortKey,
			sort_order: this.sort.sortDirection,
		}) as IOperationListPayload;
		if (id === OPERATION_TYPE.MESSAGING) {
			this.tableFilters = buildMessagingApiPayload(this.tableFilters);
		}
		this.toolbarHelperService.cleanSavedToolbar(this.constructor.name);
	}

	public tableCell(cell: ITableCellEmitter): void {
		this.goToDetail(cell.rowId, cell.event);
	}

	public tableRow(rowClick: ITableRow & { event?: MouseEvent }): void {
		this.goToDetail(rowClick.clickValue, rowClick.event);
	}

	private goToDetail(id: any, event?: MouseEvent | null): void {
		const {
			operation_types_uuid,
			sort_by,
			sort_order,
			document_type,
			messaging_channel,
			...tableFiltersWithoutOperationType
		} = this.tableFilters || {};
		// Si el document_type viene del tab (docTypeSelected), no lo contamos como filtro de usuario
		// para que saveToolbarPagination lo trate como "sin filtros" y llame getList() directamente al restaurar
		const userFilters = this.docTypeSelected
			? tableFiltersWithoutOperationType
			: {
					...tableFiltersWithoutOperationType,
					...(document_type !== undefined ? { document_type } : {}),
					...(messaging_channel !== undefined ? { messaging_channel } : {}),
				};
		const basePath = '/' + (this.location?.path().split('/').filter(Boolean)[0] ?? 'operations');
		this.toolbarHelperService.saveToolbarPagination(
			userFilters,
			this.toolbar,
			this.config.pagination,
			basePath,
			this.docTypeSelected,
			this.constructor.name,
		);

		this.navigationService.navigateTo(
			{
				routeSegments: ['operations', 'detail', id],
				...(this.id === OPERATION_TYPE.MESSAGING ? { extras: { queryParams: { entity: 'tfa' } } } : {}),
			},
			event,
		);
	}

	public sortChange(event: ITableSortEvent): void {
		// Si el sortDirection es nullish, significa que se ha desactivado el ordenamiento
		if (!event.sortDirection) this.sort = this.operationHelper.resetSort();
		else this.sort = event;

		this.getList();
	}

	public override filterChange(filters: any): void {
		this.tableFilters = transformTrimObject(filters);
		this.setListConfigPagination(
			this.hasPrevPagination
				? { current_page: this.config?.pagination?.page, total: this.config?.pagination?.total }
				: { current_page: 1 },
		);
		this.hasPrevPagination = false;
		this.getList();
	}

	// TITLE
	private setOperationTitleGroup(): { title: string; tooltip: string } {
		const types = {
			[OPERATION_TYPE.FULL]: () => ({
				title: this.trans.instant('OPERATIONS.DATA'),
				tooltip: this.trans.instant('OPERATIONS.DATA_TOOLTIP'),
			}),
			[OPERATION_TYPE.OCR]: () => ({
				title: this.trans.instant('OPERATIONS.OCR'),
				tooltip: this.trans.instant('OPERATIONS.OCR_TOOLTIP'),
			}),
			[OPERATION_TYPE.POL]: () => ({
				title: this.trans.instant('OPERATIONS.POL'),
				tooltip: this.trans.instant('OPERATIONS.POL_TOOLTIP'),
			}),
			[OPERATION_TYPE.DOCUMENTS]: () => ({
				title: this.trans.instant('OPERATIONS.DOCUMENTS'),
				tooltip: this.trans.instant('OPERATIONS.DOCUMENTS_TOOLTIP'),
			}),
			[OPERATION_TYPE.CONNECTIONS]: () => ({
				title: this.trans.instant('OPERATIONS.CONNECTIONS'),
				tooltip: this.trans.instant('OPERATIONS.CONNECTIONS_TOOLTIP'),
			}),
			[OPERATION_TYPE.FINANCIAL_EVALUATION]: () => ({
				title: this.trans.instant('OPERATIONS.FINANCIAL_EVALUATION'),
				tooltip: this.trans.instant('OPERATIONS.FINANCIAL_EVALUATION_TOOLTIP'),
			}),
			[OPERATION_TYPE.FORM]: () => ({
				title: this.trans.instant('OPERATIONS.FORMS'),
				tooltip: this.trans.instant('OPERATIONS.FORMS_TOOLTIP'),
			}),
			[OPERATION_TYPE.MESSAGING]: () => ({
				title: this.trans.instant('OPERATIONS.MESSAGING'),
				tooltip: this.trans.instant('OPERATIONS.MESSAGING_TOOLTIP'),
			}),
			['default']: () => ({
				title: this.trans.instant('OPERATIONS.DATA'),
				tooltip: this.trans.instant('OPERATIONS.DATA_TOOLTIP'),
			}),
		};
		const id = this.id ? this.id.toUpperCase() : 'default';
		return types[id] ? types[id]() : null;
	}

	public tabSelected(tab: { id: string; label: string }): void {
		this.docTypeSelected = tab.id;
		this.filterChange({ ...this.tableFilters, document_type: this.docTypeSelected });

		this.tableFilters = undefined;
		this.updateToolbar();
	}

	private updateToolbar(): void {
		this.toolbar = undefined;
		this.tableFilters = undefined;
		setTimeout(() => {
			this.loading.hide();
			const savedToolbar = this.toolbarHelperService.getToolbar(this.constructor.name);
			if (savedToolbar?.tab) {
				this.docTypeSelected = savedToolbar.tab;
				this.tabs = this.tabs.map((t) => ({ ...t, selected: t.id === this.docTypeSelected }));
			}
			this.toolbarHelperService.initializeListToolbar(this, () =>
				this.operationHelper.getToolbar(this.id as OPERATION_TYPE, this.docTypeSelected as DOCS_TYPE),
			);
		}, 10);
	}

	private getOperationTypesUuids(ids: string[]): string[] {
		ids = ids.map((id) => id.toLowerCase());
		return (this.masterService.operationsTypes || [])
			.filter((operation: ISelect) => ids.includes(operation.value?.toLowerCase() || ''))
			.map((operation: ISelect) => operation.id.toString());
	}
}
